import React, { useState, useEffect } from 'react';
import {
  Box,
  VStack,
  HStack,
  Text,
  SimpleGrid,
  Badge,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  Alert,
  AlertIcon,
  AlertDescription,
  useDisclosure,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  ModalFooter,
  Table,
  Tbody,
  Tr,
  Td,
  List,
  ListItem,
  ListIcon,
  Tooltip,
  IconButton,
  Input,
  Progress,
} from '@chakra-ui/react';
import {
  FiUsers,
  FiBook,
  FiEdit,
  FiTrash2,
  FiSave,
  FiEye,
  FiClock,
  FiCheckCircle,
  FiAlertCircle,
  FiUserPlus,
  FiBookOpen,
} from 'react-icons/fi';
import { useNavigate, useParams } from 'react-router-dom';

import {
  AdminLayout,
  Card,
  Button,
  StatsCard,
  FormField,
  SelectInput,
  DataTable,
  TableColumn,
} from '@grcs/components';

import { useToast } from '@grcs/hook/useToast';
import { useDataByIdEffect } from '@grcs/hook/useDataByIdEffect';
import {
  ClassProfile,
  StudentProfile,
  HomeworkType,
} from '@grcs/types/DataTypes';
import { UserRole } from '@grcs/types/UserRole';
import {
  useClassOperations,
  useStudentOperations,
  useHomeworkOperations,
  useUserOperations,
} from '@grcs/hook/useDatabase';
import { TABLE_NAMES } from '@grcs/utils/database/types';

interface EnhancedClassData extends ClassProfile {
  studentCount: number;
  homeworkCount: number;
  completedHomeworkCount: number;
  avgCompletionRate: number;
  recentActivity: string;
  enrollmentTrend: 'increasing' | 'stable' | 'decreasing';
}

interface ClassStudent extends StudentProfile {
  enrollmentDate: string;
  completionRate: number;
  lastActivity: string;
  status: 'active' | 'inactive' | 'pending';
}

interface ClassHomework extends HomeworkType {
  completionRate: number;
  avgScore: number;
  status: 'published' | 'draft' | 'expired';
  article_name?: string;
  published_date?: string;
}

export const AdminEditClassPageEnhanced: React.FC = () => {
  const { classId } = useParams();
  const navigate = useNavigate();
  const { successToast, errorToast } = useToast();

  const classOps = useClassOperations();
  const studentOps = useStudentOperations();
  const homeworkOps = useHomeworkOperations();
  const userOps = useUserOperations();

  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [students, setStudents] = useState<ClassStudent[]>([]);
  const [homework, setHomework] = useState<ClassHomework[]>([]);
  const [teachers, setTeachers] = useState<any[]>([]);

  const {
    isOpen: isDeleteOpen,
    onOpen: onDeleteOpen,
    onClose: onDeleteClose,
  } = useDisclosure();
  const {
    isOpen: isPreviewOpen,
    onOpen: onPreviewOpen,
    onClose: onPreviewClose,
  } = useDisclosure();

  const { data: classData, setData: setClassData } =
    useDataByIdEffect<ClassProfile>({
      idName: 'class_token',
      idValue: classId,
      tableName: TABLE_NAMES.CLASS,
    });

  const [formData, setFormData] = useState<Partial<ClassProfile>>({});

  useEffect(() => {
    if (classData) {
      setFormData(classData);
      loadClassDetails();
      loadTeachers();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [classData]);

  const loadClassDetails = async () => {
    if (!classId) return;

    setIsLoading(true);
    try {
      // Load students in this class
      const { data: studentsData } = await studentOps.getStudentsByClassId(
        classId,
      );
      if (studentsData) {
        const enhancedStudents: ClassStudent[] = studentsData.map(
          (student: any) => ({
            ...student,
            enrollmentDate: new Date().toISOString().split('T')[0], // Simulated
            completionRate: Math.floor(Math.random() * 100),
            lastActivity: `${Math.floor(Math.random() * 30)} days ago`,
            status: Math.random() > 0.8 ? 'inactive' : ('active' as any),
          }),
        );
        setStudents(enhancedStudents);
      }

      // Load homework for this class
      const { data: homeworkData } = await homeworkOps.getHomeworkByClassId(
        classId,
      );
      if (homeworkData) {
        const enhancedHomework: ClassHomework[] = homeworkData.map(
          (hw: any) => ({
            ...hw,
            completionRate: Math.floor(Math.random() * 100),
            avgScore: Math.floor(Math.random() * 40) + 60,
            status: hw.published_date ? 'published' : ('draft' as any),
          }),
        );
        setHomework(enhancedHomework);
      }
    } catch (error) {
      console.error('Error loading class details:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const loadTeachers = async () => {
    try {
      const { data: teachersData } = await userOps.getUsersByRole(
        UserRole.TEACHER,
      );
      if (teachersData) {
        setTeachers(teachersData);
      }
    } catch (error) {
      console.error('Error loading teachers:', error);
    }
  };

  const handleInputChange = (field: keyof ClassProfile, value: any) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    setHasUnsavedChanges(true);
  };

  const handleSave = async () => {
    if (!classId || !formData) return;

    setIsSaving(true);
    try {
      const { error } = await classOps.updateDataById(
        TABLE_NAMES.CLASS,
        'class_token',
        classId,
        formData,
      );

      if (error) {
        errorToast({
          title: 'Error updating class',
          description: error.message,
        });
      } else {
        successToast({
          title: 'Class updated successfully',
          description: 'All changes have been saved.',
        });
        setHasUnsavedChanges(false);
        setClassData({ ...classData, ...formData } as ClassProfile);
      }
    } catch (error) {
      console.error('Error saving class:', error);
      errorToast({
        title: 'Error saving class',
        description: 'An unexpected error occurred.',
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!classId) return;

    try {
      await classOps.softDeleteById(TABLE_NAMES.CLASS, 'class_token', classId);

      successToast({
        title: 'Class deleted successfully',
        description: 'The class and all associated data have been removed.',
      });
      navigate('/admin/class');
    } catch (error) {
      console.error('Error deleting class:', error);
    }
    onDeleteClose();
  };

  const getEnhancedClassData = (): EnhancedClassData | null => {
    if (!classData) return null;

    const studentCount = students.length;
    const homeworkCount = homework.length;
    const completedHomeworkCount = homework.filter(
      (hw) => hw.status === 'published',
    ).length;
    const avgCompletionRate =
      homework.reduce((sum, hw) => sum + hw.completionRate, 0) /
        homeworkCount || 0;

    return {
      ...classData,
      class_token: classData.class_token || classId || '',
      class_name: classData.class_name || 'Unknown Class',
      teacher_token: classData.teacher_token || '',
      created_at: classData.created_at || new Date().toISOString(),
      semester_year: classData.semester_year || '2024',
      grade: classData.grade || '1',
      number_of_students: classData.number_of_students || 0,
      studentCount,
      homeworkCount,
      completedHomeworkCount,
      avgCompletionRate: Math.round(avgCompletionRate),
      recentActivity: '2 days ago',
      enrollmentTrend:
        studentCount > 20
          ? 'increasing'
          : studentCount < 10
          ? 'decreasing'
          : 'stable',
    };
  };

  const enhancedClass = getEnhancedClassData();

  // Student table columns
  const studentColumns: TableColumn<ClassStudent>[] = [
    {
      key: 'student_name',
      header: 'Student',
      render: (student) => (
        <VStack align="start" spacing={1}>
          <Text fontWeight="medium">
            {student.first_name} {student.last_name}
          </Text>
          <Badge
            size="sm"
            colorScheme={
              student.status === 'active'
                ? 'green'
                : student.status === 'inactive'
                ? 'red'
                : 'orange'
            }
            variant="subtle"
          >
            {student.status}
          </Badge>
        </VStack>
      ),
    },
    {
      key: 'enrollmentDate',
      header: 'Enrolled',
      render: (student) => (
        <Text fontSize="sm" color="gray.600">
          {student.enrollmentDate}
        </Text>
      ),
    },
    {
      key: 'completionRate',
      header: 'Completion Rate',
      render: (student) => (
        <VStack align="center" spacing={1}>
          <Text fontSize="sm" fontWeight="medium">
            {student.completionRate}%
          </Text>
          <Progress
            value={student.completionRate}
            size="sm"
            width="60px"
            colorScheme={
              student.completionRate >= 80
                ? 'green'
                : student.completionRate >= 60
                ? 'yellow'
                : 'red'
            }
          />
        </VStack>
      ),
    },
    {
      key: 'lastActivity',
      header: 'Last Activity',
      render: (student) => (
        <Text fontSize="sm" color="gray.500">
          {student.lastActivity}
        </Text>
      ),
    },
    {
      key: 'actions',
      header: 'Actions',
      render: () => (
        <HStack spacing={1}>
          <Tooltip label="View profile">
            <IconButton
              aria-label="View profile"
              icon={<FiEye />}
              variant="ghost"
              size="sm"
            />
          </Tooltip>
          <Tooltip label="Edit student">
            <IconButton
              aria-label="Edit student"
              icon={<FiEdit />}
              variant="ghost"
              size="sm"
            />
          </Tooltip>
        </HStack>
      ),
    },
  ];

  if (isLoading) {
    return (
      <AdminLayout>
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          minH="200px"
        >
          <Text>Loading class details...</Text>
        </Box>
      </AdminLayout>
    );
  }

  if (!enhancedClass) {
    return (
      <AdminLayout>
        <Alert status="error">
          <AlertIcon />
          <AlertDescription>Class not found.</AlertDescription>
        </Alert>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <VStack spacing={6} align="stretch">
        {/* Header */}
        <HStack justify="space-between" align="center">
          <VStack align="start" spacing={1}>
            <Text fontSize="2xl" fontWeight="bold">
              Edit Class: {enhancedClass.class_name}
            </Text>
            <Text color="gray.600">
              Manage class settings, students, and assignments
            </Text>
          </VStack>

          <HStack spacing={3}>
            {hasUnsavedChanges && (
              <Badge colorScheme="orange" variant="subtle" py={1} px={2}>
                <HStack spacing={1}>
                  <FiAlertCircle size={12} />
                  <Text fontSize="xs">Unsaved Changes</Text>
                </HStack>
              </Badge>
            )}
            <Button
              leftIcon={<FiEye />}
              variant="secondary"
              onClick={onPreviewOpen}
            >
              Preview
            </Button>
            <Button
              leftIcon={<FiSave />}
              onClick={handleSave}
              isLoading={isSaving}
              loadingText="Saving..."
              isDisabled={!hasUnsavedChanges}
            >
              Save Changes
            </Button>
            <Button
              leftIcon={<FiTrash2 />}
              colorScheme="red"
              variant="outline"
              onClick={onDeleteOpen}
            >
              Delete Class
            </Button>
          </HStack>
        </HStack>

        {/* Statistics */}
        <SimpleGrid columns={{ base: 1, md: 2, lg: 4 }} spacing={4}>
          <StatsCard
            title="Total Students"
            value={enhancedClass.studentCount.toString()}
            icon={FiUsers}
            trend={
              enhancedClass.enrollmentTrend === 'increasing'
                ? 'up'
                : enhancedClass.enrollmentTrend === 'decreasing'
                ? 'down'
                : undefined
            }
          />
          <StatsCard
            title="Total Homework"
            value={enhancedClass.homeworkCount.toString()}
            icon={FiBook}
            subtitle={`${enhancedClass.completedHomeworkCount} published`}
          />
          <StatsCard
            title="Avg Completion"
            value={`${enhancedClass.avgCompletionRate}%`}
            icon={FiCheckCircle}
          />
          <StatsCard
            title="Last Activity"
            value={enhancedClass.recentActivity}
            icon={FiClock}
          />
        </SimpleGrid>

        {/* Main Content */}
        <Tabs>
          <TabList>
            <Tab>Class Settings</Tab>
            <Tab>Students ({students.length})</Tab>
          </TabList>

          <TabPanels>
            {/* Class Settings Tab */}
            <TabPanel>
              <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={6}>
                <Card>
                  <VStack spacing={6} align="stretch">
                    <Text fontSize="lg" fontWeight="semibold">
                      Basic Information
                    </Text>

                    <FormField label="Class Name" required>
                      <Input
                        value={formData.class_name || ''}
                        onChange={(e) =>
                          handleInputChange('class_name', e.target.value)
                        }
                        placeholder="Enter class name"
                      />
                    </FormField>

                    <FormField label="Assigned Teacher" required>
                      <SelectInput
                        value={formData.teacher_token || ''}
                        onChange={(value) =>
                          handleInputChange('teacher_token', value)
                        }
                        options={[
                          { label: 'Select a teacher', value: '' },
                          ...teachers.map((teacher) => ({
                            label: `${teacher.first_name} ${teacher.last_name}`,
                            value: teacher.user_token,
                          })),
                        ]}
                        placeholder="Choose teacher"
                      />
                    </FormField>

                    <FormField
                      label="Class Token"
                      help="Unique identifier for this class"
                    >
                      <Input
                        value={formData.class_token || ''}
                        onChange={(e) =>
                          handleInputChange('class_token', e.target.value)
                        }
                        placeholder="Auto-generated class token"
                        isDisabled
                      />
                    </FormField>
                  </VStack>
                </Card>

                <Card>
                  <VStack spacing={6} align="stretch">
                    <Text fontSize="lg" fontWeight="semibold">
                      Class Status & Settings
                    </Text>

                    <Alert status="info" variant="subtle">
                      <AlertIcon />
                      <VStack align="start" spacing={1} fontSize="sm">
                        <Text fontWeight="semibold">Class Health Check</Text>
                        <List spacing={1}>
                          <ListItem>
                            <ListIcon
                              as={
                                enhancedClass.studentCount > 0
                                  ? FiCheckCircle
                                  : FiAlertCircle
                              }
                              color={
                                enhancedClass.studentCount > 0
                                  ? 'green.500'
                                  : 'orange.500'
                              }
                            />
                            {enhancedClass.studentCount} students enrolled
                          </ListItem>
                          <ListItem>
                            <ListIcon
                              as={
                                enhancedClass.homeworkCount > 0
                                  ? FiCheckCircle
                                  : FiAlertCircle
                              }
                              color={
                                enhancedClass.homeworkCount > 0
                                  ? 'green.500'
                                  : 'orange.500'
                              }
                            />
                            {enhancedClass.homeworkCount} homework assignments
                          </ListItem>
                          <ListItem>
                            <ListIcon
                              as={
                                enhancedClass.avgCompletionRate >= 70
                                  ? FiCheckCircle
                                  : FiAlertCircle
                              }
                              color={
                                enhancedClass.avgCompletionRate >= 70
                                  ? 'green.500'
                                  : 'orange.500'
                              }
                            />
                            {enhancedClass.avgCompletionRate}% average
                            completion rate
                          </ListItem>
                        </List>
                      </VStack>
                    </Alert>

                    <VStack spacing={4} align="stretch">
                      <Button
                        leftIcon={<FiUserPlus />}
                        variant="secondary"
                        onClick={() =>
                          navigate(
                            `/admin/${enhancedClass.semester_year}/class/${classId}/students/new`,
                          )
                        }
                      >
                        Add Students
                      </Button>
                      <Button
                        leftIcon={<FiBookOpen />}
                        variant="secondary"
                        onClick={() =>
                          navigate(`/admin/homework/create?class=${classId}`)
                        }
                      >
                        Create Homework
                      </Button>
                    </VStack>
                  </VStack>
                </Card>
              </SimpleGrid>
            </TabPanel>

            {/* Students Tab */}
            <TabPanel>
              <Card>
                <VStack spacing={6} align="stretch">
                  <HStack justify="space-between">
                    <VStack align="start" spacing={1}>
                      <Text fontSize="lg" fontWeight="semibold">
                        Enrolled Students
                      </Text>
                      <Text fontSize="sm" color="gray.600">
                        Manage students in this class
                      </Text>
                    </VStack>
                    <Button
                      leftIcon={<FiUserPlus />}
                      onClick={() =>
                        navigate(
                          `/admin/${enhancedClass.semester_year}/class/${classId}/students/new`,
                        )
                      }
                    >
                      Add Students
                    </Button>
                  </HStack>

                  <DataTable
                    data={students}
                    columns={studentColumns}
                    searchable
                    searchPlaceholder="Search students..."
                    emptyMessage="No students enrolled in this class yet."
                  />
                </VStack>
              </Card>
            </TabPanel>
          </TabPanels>
        </Tabs>

        {/* Delete Confirmation Modal */}
        <Modal isOpen={isDeleteOpen} onClose={onDeleteClose} isCentered>
          <ModalOverlay />
          <ModalContent>
            <ModalHeader>Delete Class</ModalHeader>
            <ModalCloseButton />
            <ModalBody>
              <Alert status="warning" mb={4}>
                <AlertIcon />
                <VStack align="start" spacing={2}>
                  <Text fontWeight="semibold">
                    This action cannot be undone
                  </Text>
                  <Text fontSize="sm">Deleting this class will:</Text>
                  <List fontSize="sm" spacing={1} ml={4}>
                    <ListItem>• Remove all student enrollments</ListItem>
                    <ListItem>• Delete all homework assignments</ListItem>
                    <ListItem>• Permanently delete all class data</ListItem>
                  </List>
                </VStack>
              </Alert>
              <Text>
                Are you sure you want to delete "{enhancedClass.class_name}"?
              </Text>
            </ModalBody>
            <ModalFooter>
              <Button variant="secondary" mr={3} onClick={onDeleteClose}>
                Cancel
              </Button>
              <Button colorScheme="red" onClick={handleDelete}>
                Delete Class
              </Button>
            </ModalFooter>
          </ModalContent>
        </Modal>

        {/* Preview Modal */}
        <Modal
          isOpen={isPreviewOpen}
          onClose={onPreviewClose}
          size="lg"
          isCentered
        >
          <ModalOverlay />
          <ModalContent>
            <ModalHeader>Class Preview</ModalHeader>
            <ModalCloseButton />
            <ModalBody>
              <VStack spacing={4} align="stretch">
                <Box p={4} bg="gray.50" borderRadius="md">
                  <Text fontWeight="semibold" mb={2}>
                    Class Information
                  </Text>
                  <Table size="sm">
                    <Tbody>
                      <Tr>
                        <Td fontWeight="medium">Class Name:</Td>
                        <Td>{formData.class_name || 'Not set'}</Td>
                      </Tr>
                      <Tr>
                        <Td fontWeight="medium">Teacher:</Td>
                        <Td>
                          {teachers.find(
                            (t) => t.user_token === formData.teacher_token,
                          )?.first_name || 'Not assigned'}{' '}
                          {teachers.find(
                            (t) => t.user_token === formData.teacher_token,
                          )?.last_name || ''}
                        </Td>
                      </Tr>
                      <Tr>
                        <Td fontWeight="medium">Students:</Td>
                        <Td>{enhancedClass.studentCount}</Td>
                      </Tr>
                      <Tr>
                        <Td fontWeight="medium">Homework:</Td>
                        <Td>{enhancedClass.homeworkCount}</Td>
                      </Tr>
                    </Tbody>
                  </Table>
                </Box>
              </VStack>
            </ModalBody>
            <ModalFooter>
              <Button onClick={onPreviewClose}>Close</Button>
            </ModalFooter>
          </ModalContent>
        </Modal>
      </VStack>
    </AdminLayout>
  );
};
